import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from 'react-fetching-library';
import { styled } from '@mui/material/styles';
import { Box, Portal, Button, Backdrop, IconButton, Typography, FormControl, Select, MenuItem, InputLabel } from '@mui/material';

import { useResponsive } from '../../../services/hooks';
import Iconify from '../../../components/Iconify';
import { FullScreenBase } from './order-details/FullScreenBase';
import { SimpleView } from './order-details/SimpleView';
import ConfirmChangeStateModal from './ConfirmChangeStateModal';
import ConfirmCloseOrder from './ConfirmCloseOrder';

import { formatDate } from '../../../services/functions';
import { updateOrderAction, createZSProductsAction, markAlertAsReadAction, submitAlertAction } from '../../../services/request';
import { updateOrder, setOrder, setAllowAutoOrderUpdate } from '../../../services/redux-actions';

import DownloadDocumentsModal from './DownloadDocumentsModal';
import { CreateProductsForm } from './CreateProductsForm';
import { DialogAnimate } from '../../../components/animate';
import { AlertDetails } from './AlertDetails';
import toast from 'react-hot-toast';

const RootStyle = styled('div')(({ theme }) => ({
  right: 0,
  bottom: 0,
  zIndex: 1229,
  minHeight: 440,
  outline: 'none',
  display: 'flex',
  position: 'fixed',
  overflow: 'hidden',
  flexDirection: 'column',
  margin: theme.spacing(3),
  boxShadow: theme.customShadows.z20,
  borderRadius: Number(theme.shape.borderRadius) * 2,
  backgroundColor: theme.palette.background.paper
}));

OrderDetails.propTypes = {
  isOpenCompose: PropTypes.bool,
  onCloseCompose: PropTypes.func,
  updateOrders: PropTypes.func
};

export default function OrderDetails({ isOpenCompose, onCloseCompose, parentFullScreen, updateOrders, refreshOrders, setSelectionModel }) {
  const dispatch = useDispatch();
  const { module } = useSelector(state => state.app);
  const { order } = useSelector(state => state.orderData);
  const { loading, mutate } = useMutation(updateOrderAction);
  const createZSProducts = useMutation(createZSProductsAction);
  const mutateAlert = useMutation(markAlertAsReadAction);
  const mutateCreateAlert = useMutation(submitAlertAction);
  const [fullScreen, setFullScreen] = React.useState(false);
  const [state, setState] = React.useState(order.state);
  const [openStateModal, setOpenStateModal] = React.useState(false);
  const [openCreateProductsModal, setOpenCreateProductsModal] = React.useState(false);
  const [openOrderAlertModal, setOpenOrderAlertModal] = React.useState(false);
  const [openConfirmCloseOrderModal, setOpenConfirmCloseOrderModal] = React.useState(false);
  const [openDownloadModal, setOpenDownloadModal] = React.useState(false);
  const refFormik = React.useRef();

  const isDesktop = useResponsive('up', 'sm');


  const handleOpenStateModal = () => setOpenStateModal(true);
  const handleCloseStateModal = () => {
    setState(order.state);
    setOpenStateModal(false);
  };

  const handleOpenConfirmCloseOrderModal = () => setOpenConfirmCloseOrderModal(true);
  const handleCloseConfirmCloseOrderModal = () => setOpenConfirmCloseOrderModal(false);

  const handleOpenDownloadModal = () => setOpenDownloadModal(true);
  const handleCloseDownloadModal = () => setOpenDownloadModal(false);

  const handleStateSubmit = async (orderId, values) => {
    setOpenStateModal(false);
    const { error, payload } = await mutate(orderId, values);
    if (!error) {
      refFormik.current.resetForm({
        values: { ...refFormik.current.values,
          positions: payload.products.map(p => ({ status: 'OLD', ...p })),
          final_payment_date: payload.final_payment_date,
          advance_payment_date: payload.advance_payment_date }
      });
      dispatch(updateOrder(payload));
      updateOrders(payload);
      if (order.way_of_sending_contract === 'traditional_mail' && payload.state === 'CON') {
        handleOpenDownloadModal();
        toast.success('Status został zmieniony. Dokumenty powinny zostać wysłane pocztą tradycyjną');
      } else {
        toast.success('Status został zmieniony');
      }
    } else {
      setState(order.state);
      const body = {
        order: orderId,
        module,
        title: `Nie udało się zmienić statusu dla zamówienia ${order.identifier}`
      };
      if (_.isArray(payload)) {
        toast.error(payload[0]);
        if (payload[0] != 'Aby zmienić status na potwierdzony należy zaznaczyć zakwaterowano') {
          mutateCreateAlert.mutate({ ...body, content: payload[0] });
        }
      } else {
        toast.error('Nie udało się zmienić statusu');
        mutateCreateAlert.mutate({ ...body, content: 'Błąd wewnętrzny' });
      }
    }
  };

  React.useEffect(() => {
    setState(order.state);
    if (module == 'zielone_szkoly') {
      if (order.products_created === false) {
        setOpenCreateProductsModal(true);
      } else {
        setOpenCreateProductsModal(false);
      }
    }
    if (order.latest_alert) {
      setOpenOrderAlertModal(true);
    }
  }, [order]);

  React.useEffect(() => {
    setFullScreen(parentFullScreen);
  }, [parentFullScreen]);

  const handleChangeState = event => {
    setState(event.target.value);
    handleOpenStateModal();
  };

  const handleExitFullScreen = () => {
    setFullScreen(false);
  };

  const handleEnterFullScreen = () => {
    setFullScreen(true);
  };

  const handleClose = () => {
    setSelectionModel([order.id]);
    onCloseCompose();
    setFullScreen(false);
    handleCloseConfirmCloseOrderModal();
    dispatch(setOrder({}));
    dispatch(setAllowAutoOrderUpdate(true));
    refreshOrders();
  };

  const handleConfirmClose = () => {
    if (fullScreen) {
      if (refFormik.current.dirty) {
        handleOpenConfirmCloseOrderModal();
      } else {
        handleClose();
      }
    } else {
      handleClose();
    }
  };

  const closeByESCKey = e => {
    if (e.key === 'Escape') {
      handleConfirmClose();
    }
  };

  if (!isOpenCompose) {
    return null;
  }

  const handleCreateProducts = async (participantGrossValue, guardianGrossValue) => {
    const { error, payload } = await createZSProducts.mutate(order.id, { participantGrossValue, guardianGrossValue });
    if (!error) {
      dispatch(setOrder(payload));
      setOpenCreateProductsModal(false);
    } else {
      toast.error('Nie udało się utworzyć produktów');
    }
  };

  const handleCloseOrderAlert = async markAsRead => {
    if (markAsRead) {
      const { error, payload } = await mutateAlert.mutate(order.latest_alert.id);
      if (!error) {
        setOpenOrderAlertModal(false);
      } else {
        toast.error('Nie udało się przeczytać powiadomienia');
      }
    } else {
      setOpenOrderAlertModal(false);
    }
  };

  return (
    <Portal>
      <Backdrop open={fullScreen || !isDesktop} sx={{ zIndex: 1228 }} />
      { order
        ? <RootStyle
          sx={{
            ...(fullScreen && {
              top: 0,
              left: 0,
              zIndex: 1229,
              margin: 'auto',
              width: {
                xs: 'calc(100% - 24px)',
                md: 'calc(100% - 20px)'
              },
              height: {
                xs: 'calc(100% - 24px)',
                md: 'calc(100% - 10px)'
              }
            }),
            minHeight: '300px'
          }}
        >
          <Box
            sx={{
              pl: 3,
              pr: 1,
              height: 60,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Typography variant="h6">{order.participant_name ? order.participant_name : ''}</Typography>
            <Box width={35} />
            <Typography variant="h6">{order.agreement_number ? order.agreement_number : order.identifier ? order.identifier : ''}</Typography>
            <Box width={35} />
            <Typography variant="h6">{order.event_name ? order.event_name : ''}</Typography>
            <Box width={35} />
            <Typography variant="h6">{order.camp_name ? order.camp_name.slice(0, 8) : ''}</Typography>
            <Box width={35} />
            <Typography variant="h6">{order.resort_name ? order.resort_name : ''}</Typography>
            <Box width={35} />
            <Typography variant="h6">{order.age_range ? order.age_range : ''}</Typography>
            <Box width={35} />
            <Typography variant="h6">Data: {formatDate(order.created_at)}</Typography>
            <Box width={35} />
            <Box sx={{ flexGrow: 1 }} />
            <FormControl size="small">
              <InputLabel id="state-label">Status</InputLabel>
              <Select
                labelId="state-label"
                id="state"
                value={state}
                label="state"
                onChange={handleChangeState}
              >
                {_.map(order.states, (state, code) => (
                  <MenuItem key={code} value={code}>{state}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box width={20} />
            <IconButton onClick={fullScreen ? handleExitFullScreen : handleEnterFullScreen}>
              <Iconify icon={fullScreen ? 'eva:collapse-fill' : 'eva:expand-fill'} width={20} height={20} />
            </IconButton>
            <IconButton onClick={handleConfirmClose}>
              <Iconify icon={'eva:close-fill'} width={20} height={20} />
            </IconButton>
          </Box>
          { fullScreen
            ? <FullScreenBase
              module={module}
              updateOrders={updateOrders}
              refFormik={refFormik}
              closeByESCKey={closeByESCKey}
            />
            : <SimpleView positions={order.products} />
          }
        </RootStyle>
        : null}
      <ConfirmChangeStateModal open={openStateModal} handleClose={handleCloseStateModal} state={state} handleSubmit={handleStateSubmit} loading={loading} />
      <ConfirmCloseOrder open={openConfirmCloseOrderModal} handleClose={handleCloseConfirmCloseOrderModal} handleCloseOrder={handleClose} />
      <DownloadDocumentsModal order={order} open={openDownloadModal} handleClose={handleCloseDownloadModal} />
      <DialogAnimate maxWidth="md" open={openCreateProductsModal} onClose={() => setOpenCreateProductsModal(false)}>
        <CreateProductsForm handleSubmit={handleCreateProducts} loading={createZSProducts.loading} />
      </DialogAnimate>
      <DialogAnimate maxWidth="md" open={openOrderAlertModal} onClose={() => setOpenOrderAlertModal(false)}>
        <AlertDetails handleSubmit={handleCloseOrderAlert} loading={mutateAlert.loading} title={order.latest_alert ? order.latest_alert.title : ''} content={order.latest_alert ? order.latest_alert.content : ''} />
      </DialogAnimate>
    </Portal>
  );
}
